import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import usePermission from 'src/hooks/usePermissions';
import {
  SupplierWith680And684Permissions,
  SupplierWith680And820Permissions,
} from 'src/sections/manage_suppliers/utils/supplierInfoPermissions';
import { mapEditFormToOfflineUpsertRequest } from 'src/services/manage_suppliers/mapOfflineUpsertRequest';
import { formatCurrentSupplierValidationDate } from 'src/services/manage_suppliers/supplierFilterItemMappers';
import { toShowUnlinkedIndicator } from 'src/services/manage_suppliers/supplierMapper';
import useSendOfflineSupplierEmailVerificationMutation from 'src/services/manage_suppliers/useSendOfflineSupplierEmailVerificationMutation';
import useUpsertOfflineSupplierMutation from 'src/services/manage_suppliers/useUpsertOfflineSupplierMutation';
import { validateOfflineApId } from 'src/services/manage_suppliers/useValidateOfflineApIdMutation';
import { validateOfflineSupplierName } from 'src/services/manage_suppliers/useValidateOfflineSupplierNameMutation';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import {
  SupplierEmailValidationStatus,
  SupplierFormFieldKey,
  SupplierFormValues,
  SupplierTransmissionType,
} from 'src/types/manage-suppliers/SupplierForm';
import { enrichSupplierForEdit, mapSupplierToFormValues } from './utils/enrichSupplierForEdit';
import {
  getEffectiveFieldAccess,
  getSupplierEditAccess,
  resolveStatusForEditAccess,
  viewOnlySupplierFieldAccess,
} from './utils/supplierEditAccess';
import { getSupplierFormSyncErrors } from './utils/supplierFormValues';
import useSupplierRequiredFields from './useSupplierRequiredFields';

type FormField = SupplierFormFieldKey;
type AsyncField = 'supplierName' | 'apId';

type CheckApIdOptions = {
  /** When true, validates even if AP ID matches the initial value (e.g. external name edit). */
  forceValidate?: boolean;
};

interface UseEditSupplierFormOptions {
  supplier?: ManageSupplier | null;
  isOpen: boolean;
  onSuccess?: () => void | Promise<void>;
  /** When true, all fields start read-only until the user clicks Edit. */
  viewUntilEdit?: boolean;
  /** Gate for showing the Edit button (e.g. View Catalogs page permissions). */
  canEnterEditMode?: boolean;
  /** Custom save handler for online supplier overrides (e.g. View Catalogs). */
  saveOnlineOverride?: (values: SupplierFormValues, supplierClientId: number) => Promise<void>;
}

export const useEditSupplierForm = ({
  supplier = null,
  isOpen,
  onSuccess,
  viewUntilEdit = false,
  canEnterEditMode = true,
  saveOnlineOverride,
}: UseEditSupplierFormOptions) => {
  const { t } = useTranslation();
  const has680And684 = usePermission({ permissions: SupplierWith680And684Permissions });
  const has680And820 = usePermission({ permissions: SupplierWith680And820Permissions });
  const hasEditPermission = has680And684 || has680And820;
  const requiredFields = useSupplierRequiredFields();
  const { mutateAsync: upsertOfflineSupplier, isPending: isSaving } =
    useUpsertOfflineSupplierMutation();
  const { mutateAsync: sendEmailVerification, isPending: isResendingValidation } =
    useSendOfflineSupplierEmailVerificationMutation();

  const enrichedSupplier = useMemo(
    () => (supplier ? enrichSupplierForEdit(supplier) : null),
    [supplier]
  );

  const [values, setValues] = useState<SupplierFormValues | null>(null);
  const [initialValues, setInitialValues] = useState<SupplierFormValues | null>(null);
  const [touched, setTouched] = useState<Partial<Record<FormField, boolean>>>({});
  const [asyncErrors, setAsyncErrors] = useState<Partial<Record<AsyncField, string>>>({});
  const [emailValidationStatus, setEmailValidationStatus] =
    useState<SupplierEmailValidationStatus>('none');
  const [emailValidationDateTime, setEmailValidationDateTime] = useState<string | undefined>();
  const [emailValidationExpired, setEmailValidationExpired] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const validationCacheRef = useRef<
    Partial<Record<AsyncField, { value: string; isDuplicate: boolean }>>
  >({});
  const preservedEmailValidationRef = useRef<{
    status: SupplierEmailValidationStatus;
    dateTime?: string;
    expired: boolean;
  } | null>(null);

  const editAccess = useMemo(() => {
    if (!enrichedSupplier) {
      return null;
    }

    return getSupplierEditAccess({
      supplier: {
        ...enrichedSupplier,
        status: resolveStatusForEditAccess(enrichedSupplier.status, values?.status),
      },
      canManageSuppliers: has680And684,
      canManageTransmissionSettings: has680And820,
      hasEditPermission,
    });
  }, [has680And684, has680And820, enrichedSupplier, hasEditPermission, values?.status]);

  const resetFromSupplier = useCallback(() => {
    if (!enrichedSupplier) {
      return;
    }

    const nextValues = mapSupplierToFormValues(enrichedSupplier);
    setValues(nextValues);
    setInitialValues(nextValues);
    setTouched({});
    setAsyncErrors({});
    validationCacheRef.current = {};
    setEmailValidationStatus(enrichedSupplier.emailValidationStatus);
    setEmailValidationDateTime(enrichedSupplier.emailValidationDateTime);
    setEmailValidationExpired(enrichedSupplier.emailValidationExpired ?? false);
    preservedEmailValidationRef.current = {
      status: enrichedSupplier.emailValidationStatus,
      dateTime: enrichedSupplier.emailValidationDateTime,
      expired: enrichedSupplier.emailValidationExpired ?? false,
    };
  }, [enrichedSupplier]);

  useEffect(() => {
    if (isOpen && enrichedSupplier) {
      resetFromSupplier();
    }
  }, [enrichedSupplier, isOpen, resetFromSupplier]);

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
    }
  }, [isOpen]);

  const exitEditMode = useCallback(() => {
    resetFromSupplier();
    setIsEditing(false);
  }, [resetFromSupplier]);

  const stayInViewWithSavedValues = useCallback((saved: SupplierFormValues) => {
    setValues(saved);
    setInitialValues(saved);
    setTouched({});
    setAsyncErrors({});
    validationCacheRef.current = {};
    setIsEditing(false);
  }, []);

  const fieldAccess = useMemo(() => {
    if (!editAccess) {
      return viewOnlySupplierFieldAccess;
    }

    if (viewUntilEdit && !isEditing) {
      return viewOnlySupplierFieldAccess;
    }

    return editAccess.fields;
  }, [editAccess, isEditing, viewUntilEdit]);

  const canShowEditButton = Boolean(
    viewUntilEdit && canEnterEditMode && !isEditing && hasEditPermission && editAccess?.canSave
  );

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const setFieldValue = useCallback(
    (field: FormField, value: string | SupplierTransmissionType) => {
      setValues((prev) => {
        if (!prev) {
          return prev;
        }

        const nextValues = { ...prev, [field]: value };

        if (field === 'supplierName' || field === 'apId') {
          delete validationCacheRef.current[field];
        }

        if (field === 'externalSupplierName') {
          delete validationCacheRef.current.apId;
        }

        return nextValues;
      });
    },
    []
  );

  const resolvedEmailValidation = useMemo(() => {
    if (!values || !enrichedSupplier) {
      return {
        status: 'none' as SupplierEmailValidationStatus,
        dateTime: undefined,
        expired: false,
      };
    }

    const emailMatchesOriginal = values.orderEmail.trim() === enrichedSupplier.email.trim();

    if (!emailMatchesOriginal) {
      return {
        status: emailValidationStatus,
        dateTime: emailValidationDateTime,
        expired: emailValidationExpired,
      };
    }

    if (emailValidationStatus !== 'none') {
      return {
        status: emailValidationStatus,
        dateTime: emailValidationDateTime,
        expired: emailValidationExpired,
      };
    }

    const preserved = preservedEmailValidationRef.current;
    if (preserved && preserved.status !== 'none') {
      return preserved;
    }

    return {
      status: enrichedSupplier.emailValidationStatus,
      dateTime: enrichedSupplier.emailValidationDateTime,
      expired: enrichedSupplier.emailValidationExpired ?? false,
    };
  }, [
    emailValidationDateTime,
    emailValidationExpired,
    emailValidationStatus,
    enrichedSupplier,
    values,
  ]);

  const applyDuplicateResult = useCallback(
    (field: AsyncField, value: string, isDuplicate: boolean) => {
      validationCacheRef.current[field] = { value, isDuplicate };
      setAsyncErrors((prev) => {
        const next = { ...prev };
        if (isDuplicate) {
          next[field] =
            field === 'supplierName'
              ? t('This name already exists. Please enter another.')
              : t('This ID already exists. Please enter another.');
        } else {
          delete next[field];
        }
        return next;
      });
      return !isDuplicate;
    },
    [t]
  );

  const checkSupplierName = useCallback(
    async (supplierName: string) => {
      if (!editAccess?.fields.supplierName) {
        return true;
      }

      const trimmed = supplierName.trim();
      if (!trimmed || trimmed === initialValues?.supplierName.trim()) {
        delete validationCacheRef.current.supplierName;
        setAsyncErrors((prev) => {
          const next = { ...prev };
          delete next.supplierName;
          return next;
        });
        return true;
      }

      const cached = validationCacheRef.current.supplierName;
      if (cached?.value === trimmed) {
        return applyDuplicateResult('supplierName', trimmed, cached.isDuplicate);
      }

      const isDuplicate = await validateOfflineSupplierName({
        supplierName: trimmed,
        ...(enrichedSupplier?.offlineSupplierId != null && {
          offlineSupplierId: enrichedSupplier.offlineSupplierId,
        }),
      });

      return applyDuplicateResult('supplierName', trimmed, isDuplicate);
    },
    [
      applyDuplicateResult,
      editAccess?.fields.supplierName,
      enrichedSupplier?.offlineSupplierId,
      initialValues?.supplierName,
    ]
  );

  const checkApId = useCallback(
    async (apId: string, options?: CheckApIdOptions) => {
      if (!editAccess?.fields.apId) {
        return true;
      }

      const trimmed = apId.trim();
      const forceValidate = options?.forceValidate ?? false;

      if (!trimmed) {
        delete validationCacheRef.current.apId;
        setAsyncErrors((prev) => {
          const next = { ...prev };
          delete next.apId;
          return next;
        });
        return true;
      }

      if (!forceValidate && trimmed === initialValues?.apId.trim()) {
        delete validationCacheRef.current.apId;
        setAsyncErrors((prev) => {
          const next = { ...prev };
          delete next.apId;
          return next;
        });
        return true;
      }

      const cached = validationCacheRef.current.apId;
      if (cached?.value === trimmed) {
        return applyDuplicateResult('apId', trimmed, cached.isDuplicate);
      }

      const isDuplicate = await validateOfflineApId({
        apId: trimmed,
        ...(enrichedSupplier?.supplierType !== 'offline' &&
        enrichedSupplier?.supplierClientId != null
          ? { supplierClientId: enrichedSupplier.supplierClientId }
          : {}),
        ...(enrichedSupplier?.supplierType === 'offline' &&
        enrichedSupplier?.offlineSupplierId != null
          ? { offlineSupplierId: enrichedSupplier.offlineSupplierId }
          : {}),
      });

      return applyDuplicateResult('apId', trimmed, isDuplicate);
    },
    [
      applyDuplicateResult,
      editAccess?.fields.apId,
      enrichedSupplier?.offlineSupplierId,
      enrichedSupplier?.supplierClientId,
      enrichedSupplier?.supplierType,
      initialValues?.apId,
    ]
  );

  const handleFieldBlur = useCallback(
    async (field: FormField) => {
      if (!values) {
        return;
      }

      setTouched((prev) => ({ ...prev, [field]: true }));

      if (field === 'supplierName') {
        await checkSupplierName(values.supplierName);
      }

      if (field === 'apId') {
        await checkApId(values.apId);
      }

      if (field === 'externalSupplierName' && values.apId.trim()) {
        await checkApId(values.apId, { forceValidate: true });
      }
    },
    [checkApId, checkSupplierName, values]
  );

  const effectiveFieldAccess = useMemo(
    () => (editAccess && values ? getEffectiveFieldAccess(editAccess.fields, values) : null),
    [editAccess, values]
  );

  const syncErrors = useMemo(() => {
    if (!values || !effectiveFieldAccess || !editAccess || !enrichedSupplier) {
      return {};
    }

    return getSupplierFormSyncErrors({
      values,
      fieldAccess: effectiveFieldAccess,
      requiredFields,
      hasSupplierInheritance:
        typeof toShowUnlinkedIndicator(enrichedSupplier.supplierInheritance) === 'boolean',
      t,
    });
  }, [effectiveFieldAccess, editAccess, enrichedSupplier, requiredFields, t, values]);

  const errors = useMemo(() => ({ ...syncErrors, ...asyncErrors }), [asyncErrors, syncErrors]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const isDirty = useMemo(() => {
    if (!values || !initialValues) {
      return false;
    }

    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [initialValues, values]);

  const showUnlinkedIndicator = useMemo((): boolean | undefined => {
    if (!enrichedSupplier) {
      return undefined;
    }

    return toShowUnlinkedIndicator(enrichedSupplier.supplierInheritance);
  }, [enrichedSupplier]);

  const ownershipBanner = useMemo(() => {
    if (!enrichedSupplier) {
      return undefined;
    }

    if (enrichedSupplier.createdBy === null || enrichedSupplier.createdBy === '') return;

    if (enrichedSupplier.isSameCompany) {
      return t('This profile was created by {{companyName}}', {
        companyName: enrichedSupplier.ownedByCompanyName,
      });
    }

    return t('This profile is managed by {{companyName}}', {
      companyName: enrichedSupplier.ownedByCompanyName,
    });
  }, [enrichedSupplier, t]);

  const showResendValidation = useMemo(() => {
    if (!values || !effectiveFieldAccess?.orderEmail || !enrichedSupplier?.offlineSupplierId) {
      return false;
    }

    if (viewUntilEdit && !isEditing) {
      return false;
    }

    return (
      values.transmissionType === 'email' &&
      (resolvedEmailValidation.status === 'pending' || resolvedEmailValidation.status === 'failed')
    );
  }, [
    effectiveFieldAccess?.orderEmail,
    enrichedSupplier?.offlineSupplierId,
    isEditing,
    resolvedEmailValidation.status,
    values,
    viewUntilEdit,
  ]);

  const handleResendValidationRequest = useCallback(async () => {
    const offlineSupplierId = enrichedSupplier?.offlineSupplierId;
    if (!offlineSupplierId) {
      return;
    }

    try {
      await sendEmailVerification({ offlineSupplierId });
      setEmailValidationStatus('pending');
      setEmailValidationDateTime(formatCurrentSupplierValidationDate());
      setEmailValidationExpired(false);
      toast.success(t('Validation request sent successfully.'));
    } catch {
      // API errors are surfaced by the axios response interceptor.
    }
  }, [enrichedSupplier?.offlineSupplierId, sendEmailVerification, t]);

  const runSaveOnlineOverride = useCallback(
    async (supplierClientId: number) => {
      if (!values || !saveOnlineOverride) {
        return;
      }

      try {
        await saveOnlineOverride(values, supplierClientId);
        toast.success(t('Supplier updated successfully.'));
        if (viewUntilEdit) {
          // Re-sync from the refetched supplier instead of the submitted values: clearing
          // AP ID and External Supplier Name makes the API fall back to inherited values.
          exitEditMode();
        }
        await onSuccess?.();
      } catch (error) {
        // API errors are surfaced by the axios response interceptor.
        console.error('Error saving online override:', error);
      }
    },
    [exitEditMode, onSuccess, saveOnlineOverride, t, values, viewUntilEdit]
  );

  const handleSave = useCallback(async () => {
    if (!values || !editAccess?.canSave) {
      return;
    }

    if (!enrichedSupplier) {
      return;
    }

    setTouched(
      Object.fromEntries(
        (Object.keys(values) as FormField[]).map((field) => [
          field,
          effectiveFieldAccess?.[field] ?? editAccess.fields[field],
        ])
      ) as Partial<Record<FormField, boolean>>
    );

    const externalNameChanged =
      values.externalSupplierName.trim() !== initialValues?.externalSupplierName.trim();

    const [nameOk, apIdOk] = await Promise.all([
      checkSupplierName(values.supplierName),
      checkApId(values.apId, { forceValidate: externalNameChanged }),
    ]);

    if (!nameOk || !apIdOk || !isValid) {
      return;
    }

    if (enrichedSupplier.supplierType !== 'offline') {
      if (saveOnlineOverride) {
        await runSaveOnlineOverride(enrichedSupplier.supplierClientId);
        return;
      }

      toast.error(t('Online supplier updates are not supported yet.'));
      return;
    }

    try {
      const emailChanged = values.orderEmail.trim() !== enrichedSupplier.email.trim();
      const transmissionChangedToEmail =
        enrichedSupplier.transmissionType !== 'email' && values.transmissionType === 'email';

      await upsertOfflineSupplier(
        mapEditFormToOfflineUpsertRequest({
          values,
          supplier: enrichedSupplier,
          fieldAccess: effectiveFieldAccess ?? editAccess.fields,
        })
      );

      if (
        values.transmissionType === 'email' &&
        values.orderEmail.trim() &&
        (emailChanged || transmissionChangedToEmail) &&
        enrichedSupplier.offlineSupplierId != null
      ) {
        try {
          await sendEmailVerification({
            offlineSupplierId: enrichedSupplier.offlineSupplierId,
            skipInvalidate: true,
          });
          setEmailValidationStatus('pending');
          setEmailValidationDateTime(formatCurrentSupplierValidationDate());
          setEmailValidationExpired(false);
        } catch {
          // API errors are surfaced by the axios response interceptor.
          return;
        }
      }

      toast.success(t('Supplier updated successfully.'));
      if (viewUntilEdit) {
        stayInViewWithSavedValues(values);
      }
      await onSuccess?.();
    } catch {
      // API errors are surfaced by the axios response interceptor.
    }
  }, [
    checkApId,
    checkSupplierName,
    editAccess,
    enrichedSupplier,
    initialValues,
    isValid,
    onSuccess,
    runSaveOnlineOverride,
    saveOnlineOverride,
    stayInViewWithSavedValues,
    sendEmailVerification,
    t,
    upsertOfflineSupplier,
    values,
    viewUntilEdit,
    effectiveFieldAccess,
  ]);

  return {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    isEditing,
    editAccess,
    fieldAccess,
    requiredFields,
    canShowEditButton,
    ownershipBanner,
    showUnlinkedIndicator,
    emailValidationStatus: resolvedEmailValidation.status,
    emailValidationDateTime: resolvedEmailValidation.dateTime,
    emailValidationExpired: resolvedEmailValidation.expired,
    showResendValidation,
    isResendingValidation,
    enrichedSupplier,
    setFieldValue,
    handleFieldBlur,
    handleSave,
    handleEditClick,
    exitEditMode,
    handleResendValidationRequest,
  };
};
